import { useParams, useNavigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import icArrowLeft from "@/assets/icons/ic-arrow-narrow-left.svg";
import icCalendar from "@/assets/icons/ic-calendar.svg";
import icPhone from "@/assets/icons/ic-phone.svg";
import icMail from "@/assets/icons/ic-mail.svg";
import icThunder from "@/assets/icons/ic-thunder.svg";
import IcArrRight from "@/assets/icons/ic-chevron-right-01.svg";
import {
  UserManagementService,
  User,
  UserPrjRes,
  UserAgentRes,
  UserInquiryRes,
  IProjectStatus,
  IAgentStatus,
  IInquiryStatus,
} from "@/services/user-management-service";
import {
  formatDate,
  formatPhoneNumber,
  STATUS_CONFIG_ADMIN,
  STATUS_CONFIG_INQUIRY,
} from "@/lib/utils";

const ManageUserDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [projects, setProjects] = useState<UserPrjRes[]>([]);
  const [agents, setAgents] = useState<UserAgentRes[]>([]);
  const [inquiries, setInquiries] = useState<UserInquiryRes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const [userResponse, projectResponse, agentResponse, inquiryResponse] = await Promise.all([
          UserManagementService.getById(id),
          UserManagementService.getListUserProject({
            user_Id: id,
            page: 1,
            page_size: 3,
          }),
          UserManagementService.getListUserAgent({
            user_Id: id,
            page: 1,
            page_size: 3,
          }),
          UserManagementService.getListUserInquiries({
            user_Id: id,
            page: 1,
            page_size: 3,
          }),
        ]);

        const userData = userResponse.data.data;
        const projectsData = projectResponse.data.data?.items ?? [];
        const agentsData = agentResponse.data.data?.items ?? [];
        const inquiriesData = inquiryResponse.data.data?.items ?? [];

        if (userData) {
          setUser(userData);
        }

        setProjects(projectsData);
        setAgents(agentsData);
        setInquiries(inquiriesData);
      } catch (err) {
        setError("Failed to load user data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleBack = () => {
    navigate("/manage-user");
  };

  const formatJoinDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    return `${year}년 ${month}월`;
  };

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG_ADMIN[status] || STATUS_CONFIG_ADMIN.in_progress;

    return (
      <div
        className={`flex items-center text-[14px] justify-center w-fit gap-1 py-[2px]  rounded ${config.bg} ${config.text} ${config.padding}`}
      >
        <p className="leading-5 font-normal">{config.label}</p>
      </div>
    );
  };

  const getInquiryStatusBadge = (status: string) => {
    const config = STATUS_CONFIG_INQUIRY[status] || STATUS_CONFIG_INQUIRY.in_progress;

    return (
      <div
        className={`flex items-center text-[14px] justify-center w-fit gap-1 py-[2px]  rounded ${config.bg} ${config.text} ${config.padding}`}
      >
        <p className="leading-5 font-normal">{config.label}</p>
      </div>
    );
  };

  if (error || !user) {
    return null;
  }

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div className="min-h-screen" style={{ display: loading ? "none" : "block" }}>
        <div className="flex items-center gap-1 mb-6">
          {/* Back Button */}
          <button onClick={handleBack} className=" w-12 h-12 flex items-center justify-center">
            <img src={icArrowLeft} alt="Back" className="" />
          </button>
          <p className="font-bold text-[#000000]">돌아가기</p>
        </div>

        {/* User Info Card */}
        <div className="bg-white rounded p-[24px] mb-6 border border-[rgba(0,0,0,0.09)]">
          <div className="flex items-start gap-6">
            {/* User Info */}
            <div className="flex-1">
              {/* Name + Status */}
              <div className="flex justify-start items-center gap-[12px] mb-[16px]">
                <h1 className="text-[24px] font-bold text-[#000000]">{user.requester_name}</h1>
                {/* <div className="bg-[#2BAA50] rounded-[1000px] px-[12px] py-[2px] text-white capitalize">
                {user.status}
              </div> */}
              </div>

              {/* Company | Department | Position - Job Title */}
              <div className="flex items-center gap-6">
                {user.company_name && (
                  <>
                    <span className="max-w-[500px] text-[#000000] after:content-[''] after:absolute after:right-[-12px] after:top-1/2 after:-translate-y-1/2 after:w-[1px] after:h-[16px] after:bg-[#00000027] relative">
                      {user.company_name}
                    </span>
                  </>
                )}
                {user.department && (
                  <>
                    <span className="text-[#000000] after:content-[''] after:absolute after:right-[-12px] after:top-1/2 after:-translate-y-1/2 after:w-[1px] after:h-[16px] after:bg-[#00000027] relative">
                      {user.department}
                    </span>
                  </>
                )}
                {user.position && (
                  <span className="text-[#000000] after:content-[''] after:absolute after:right-[-12px] after:top-1/2 after:-translate-y-1/2 after:w-[1px] after:h-[16px] after:bg-[#00000027] relative">
                    {user.position}
                  </span>
                )}
                <span className="text-[#000000]">{user.job_title}</span>
              </div>

              {/* Divider */}
              <div className="h-px bg-[#00000017 ] my-[20px]" />

              {/* Info Row with Icons */}
              <div className="flex justify-between gap-8">
                <div className="flex items-center gap-2">
                  <img src={icCalendar} alt="Calendar" className="w-5 h-5" />
                  <span className="text-[#000000]">가입일 {formatJoinDate(user.created_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <img src={icPhone} alt="Phone" className="w-5 h-5" />
                  <span className="text-[#000000]">{formatPhoneNumber(user.phone, false)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <img src={icMail} alt="Email" className="w-5 h-5" />
                  <span className="text-[#000000]">{user.email}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Projects Section - Hide if no data */}
        {projects.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[20px] font-bold text-[#000000]">
                {user.requester_name}님의 프로젝트
              </h2>
              <Link
                to={`/manage-user/${user.id}/project-list`}
                className="flex items-center text-[#000000] font-bold gap-2"
              >
                <span>View all </span>
                <img src={IcArrRight} />
              </Link>
            </div>
            <div className="mb-6">
              <div className="grid grid-cols-3 gap-[24px]">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    onClick={() => navigate(`/projects/${project.id}`)}
                    style={{
                      padding: "20px 24px 24px 24px",
                    }}
                    className="bg-white border border-[#00000017] rounded  transition-colors cursor-pointer"
                  >
                    {getStatusBadge(project.status)}
                    <span className="font-bold text-[#000000] block mb-[4px] mt-[12px] truncate">
                      {project.name}
                    </span>
                    <p className="text-[14px] text-[#000000] line-clamp-2 ">
                      {project.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Agents Section - Show only if data exists */}
        {agents.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[20px] font-bold text-[#000000]">
                {user.requester_name}님의 에이전트
              </h2>
              <Link
                to={`/manage-user/${user.id}/agent-list`}
                className="flex items-center text-[#000000] font-bold gap-2"
              >
                <span>View all </span>
                <img src={IcArrRight} />
              </Link>
            </div>
            <div className="mb-6">
              <div className="grid grid-cols-3 gap-4">
                {agents.map((agent) => (
                  <div
                    key={agent.id}
                    className="bg-white border border-[#00000017] rounded p-[24px] cursor-pointer"
                    onClick={() => navigate(`/agents/${agent.id}`)}
                  >
                    <div className="font-bold text-[#000000]">{agent.name}</div>
                    <p className="text-sm text-[#8d8d8d] mb-[24px] line-clamp-2">
                      {agent.short_description}
                    </p>
                    <div className="flex justify-between items-center">
                      {getStatusBadge(agent.status)}
                      <p className="flex text-xs text-[#000000]">
                        <img className="mr-[4px]" src={icThunder}></img> 사용 횟수:{" "}
                        {agent.selected_count}회
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Inquiry History Section - Show only if data exists */}
        {inquiries.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[20px] font-bold text-[#000000]">문의 이력</h2>
              <Link
                to={`/manage-user/${user.id}/inquiry-list`}
                className="flex items-center text-[#000000] font-bold gap-2"
              >
                <span>View all </span>
                <img src={IcArrRight} />
              </Link>
            </div>
            <div>
              <div className="grid grid-cols-3 gap-[24px]">
                {inquiries.map((inquiry) => (
                  <div
                    key={inquiry.id}
                    style={{
                      padding: "20px 24px 24px 24px",
                    }}
                    className="bg-white border border-[#00000017] rounded cursor-pointer"
                    onClick={() => navigate(`/inquiries/${inquiry.id}`)}
                  >
                    <p className="font-bold text-[#000000] mb-[12px] truncate">{inquiry.title}</p>
                    <div className="flex justify-between items-center">
                      {getInquiryStatusBadge(inquiry.status)}
                      <p className="flex justify-end gap-[4px] text-xs text-[#000000]">
                        <img src={icCalendar} className="w-4 h-4" />
                        <span>{formatDate(inquiry.created_at)}</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ManageUserDetailPage;
